// Angular
import { AbstractControl, FormGroup } from "@angular/forms";
// Libraries
import * as moment from 'moment';
// Afidium
import { ITextCode } from 'libraries/service/resources/model/search/ITextCode';
import { IDateRange } from 'libraries/service/resources/model/search/IDateRange';
import { DatesService } from "app_sasha/shared/services/dates/dates.service";
import { BookingSearchParameters, IConfigFormArray } from 'app_sasha/shared/models/BookingSearchParameters';

export interface IPatchTypeConfig {
    date?: string[];
    codeText?: string[];
    array?: string[];
    formArray?: string[];
}

export type FieldValue = IDateRange | ITextCode | string[];

export class SearchUtils {

    public static getStringArrayValue<TParameters>(params: TParameters, name: string): string[] {
        if (params && params[name]) {
            if (Array.isArray(params[name])) {
                return params[name];
            }

            if (typeof params[name] === "string") {
                return [params[name]];
            }
        }

        return undefined;
    }
    public static getNumberValue<TParameters>(params: TParameters, name: string): string[] {
        if (params && params[name]) {
            if(params[name] || params[name] === 0) {
                return params[name];
            }
        }

        return undefined;
    }

    public static getTextCodeValue<TParameters>(params: TParameters, name: string): ITextCode {
        const textParamName: string = `${name}text`;
        const codeParamName: string = `${name}code`;

        if (params && (params[textParamName] && params[textParamName] !== '') ||
            (params[codeParamName] && params[codeParamName] !== '')) {

            return {
                code: params[codeParamName] && params[codeParamName] !== '' ? params[codeParamName] : '',
                text: params[textParamName] && params[textParamName] !== '' ? params[textParamName] : '',
            };
        }

        return undefined;
    }

    public static setTextValue<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: string,
        newPropertyName: string): TUrlParameters {
        const value: string = propertyValue && propertyValue.length ? propertyValue : undefined;

        if (value !== undefined) {
            return { [newPropertyName]: value, ...urlParameters };
        }

        return urlParameters;
    }
    public static setNumberValue<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: number,
        newPropertyName: string): TUrlParameters {
        const value: number = propertyValue || propertyValue === 0 ? propertyValue : undefined;

        if (value !== undefined) {
            return { [newPropertyName]: value, ...urlParameters };
        }

        return urlParameters;
    }

    /**
     * @param params parameters that contain the min/max values required to build the IDateRange
     * @param name prefix of min/max parameter names
     * @param datesService has to be provided if date expression support is required, otherwise can be omited.
     */
    public static getDateRangeValue<TParameters>(params: TParameters, name: string, datesService?: DatesService): IDateRange {
        const missingParamName: string = `${name}missing`;
        const minParamName: string = `${name}min`;
        const maxParamName: string = `${name}max`;

        if (params && params[missingParamName] || params[minParamName] || params[maxParamName]) {
            const output: IDateRange = {};

            if (params[missingParamName]) {
                output.missing = params[missingParamName] === 'true';
            }

            if (params[minParamName] && params[minParamName] !== '') {
                output.min = datesService ? datesService.convertExpressionToStaticDateStr(params[minParamName]) : params[minParamName];
            }

            if (params[maxParamName] && params[maxParamName] !== '') {
                output.max = datesService ? datesService.convertExpressionToStaticDateStr(params[maxParamName]) : params[maxParamName];
            }

            return output;
        }

        return undefined;
    }

    public static getBooleanValue<TParameters>(params: TParameters, name: string): boolean {
        if (params && (params[name] === 'true' || params[name] === 'false')) {
            return params[name] === 'true';
        }

        return undefined;
    }

    public static setArrayValueIfNotEmpty<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: string[],
        newPropertyName: string): TUrlParameters {
        const value: string[] = propertyValue && propertyValue.length ?
            propertyValue :
            undefined;

        if (newPropertyName === "afs") {

            return urlParameters;
        }

        if (value !== undefined) {
            return { [newPropertyName]: value, ...urlParameters };
        }

        return urlParameters;
    }

    public static setTextCodeValueIfNotEmpty<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: ITextCode,
        newPropertyName: string): TUrlParameters {

        const text: string[] = propertyValue && propertyValue.text && propertyValue.text.length ?
            propertyValue.text :
            undefined;

        const code: string[] = propertyValue && propertyValue.code && propertyValue.code.length ?
            propertyValue.code :
            undefined;

        if (text !== undefined || code !== undefined) {
            return {
                ...urlParameters,
                ...(text ? { [`${newPropertyName}text`]: text } : null),
                ...(code ? { [`${newPropertyName}code`]: code } : null),
            };
        }

        return urlParameters;
    }

    public static setFormArrayValueIfNotEmpty<TUrlParameters>(
        newPropertyName: string,
        propertyValue: string[],
        urlParameters: TUrlParameters): TUrlParameters {
        const serviceName: IConfigFormArray[] = BookingSearchParameters.AFIDIUM_SERVICE_PROPERTIES;
        const value: string[] = propertyValue && propertyValue.length ?
            propertyValue :
            undefined;
        const newParametersString: {} = {};
        const newParametersDate: IDateRange = {};
        let returnTrue: boolean = false;
        if (value !== undefined && propertyValue !== undefined) {
            serviceName.forEach((item: IConfigFormArray) => {
                propertyValue.forEach((propertyItem: string, propertyIndex: number) => {
                    if (propertyItem[item.name] !== undefined && propertyItem[item.name] !== null) {
                        if (propertyItem[item.name].length !== 0) {
                            if (item.type === 'string') {
                                if (item.name === 'serviceCodes') {
                                    returnTrue = true;
                                }
                                const servicePropertyName: string = `${newPropertyName}.${propertyIndex}.${item.shortName}`;
                                newParametersString[servicePropertyName] = propertyItem[item.name];
                            }
                            else if (item.type === 'date') {
                                const minPropertyName: string = `${newPropertyName}.${propertyIndex}.${item.shortName}min`;
                                const maxPropertyName: string = `${newPropertyName}.${propertyIndex}.${item.shortName}max`;
                                if (propertyItem[item.name].min) {
                                    newParametersDate[minPropertyName] = moment(propertyItem[item.name].min).format('YYYY-MM-DD');
                                }
                                if (propertyItem[item.name].max) {
                                    newParametersDate[maxPropertyName] = moment(propertyItem[item.name].max).format('YYYY-MM-DD');
                                }
                            }
                        }
                    }

                });
            });
            if (returnTrue) {
                return { ...newParametersString, ...newParametersDate, ...urlParameters };
            }

        }
        return urlParameters;
    }

    public static setDateRangeValueIfNotEmpty<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: IDateRange,
        newPropertyName: string): TUrlParameters {
        if (propertyValue !== undefined) {
            const value: IDateRange = propertyValue.min && propertyValue.min !== ''
            || propertyValue.max && propertyValue.max !== ''
            || propertyValue.missing ?
                propertyValue :
                undefined;

            const missingPropertyName: string = `${newPropertyName}missing`;
            const minPropertyName: string = `${newPropertyName}min`;
            const maxPropertyName: string = `${newPropertyName}max`;

            if (value !== undefined) {
                const newParameters: IDateRange = {};

                if (value.missing) {
                    newParameters[missingPropertyName] = value.missing;
                }

                if (value.min && value.min !== '' && value.min !== null) {
                    newParameters[minPropertyName] = moment(value.min).format('YYYY-MM-DD');
                }

                if (value.max && value.max !== '' && value.max !== null) {
                    newParameters[maxPropertyName] = moment(value.max).format('YYYY-MM-DD');
                }

                return { ...newParameters, ...urlParameters };
            }
        }

        return urlParameters;
    }

    public static setDateValueIfNotEmpty<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: Date,
        newPropertyName: string): TUrlParameters {

        const value: Date = propertyValue ? propertyValue : undefined;

        if (value !== undefined) {
            return { [newPropertyName]: moment(value).format('YYYY-MM-DD'), ...urlParameters };
        }

        return urlParameters;
    }

    public static setValueIfNotEmpty<TUrlParameters>(
        urlParameters: TUrlParameters,
        propertyValue: unknown,
        newPropertyName: string): TUrlParameters {

        const value: unknown = propertyValue !== null && propertyValue !== undefined ?
            propertyValue :
            undefined;

        if (value !== undefined) {
            return { [newPropertyName]: value, ...urlParameters };
        }

        return urlParameters;
    }

    public static populateSearchParametersFromForm<TParameters>(form: FormGroup, params: TParameters): TParameters {
        const fields: { [key: string]: AbstractControl } = form.controls;

        Object.keys(fields).forEach((fieldName: string): void => {
            if (fields[fieldName].value !== "" && fields[fieldName].value !== "Invalid date") {
                params[fieldName] = fields[fieldName].value;
            }
        });

        return params;
    }

    public static getArrayValue(value: string | string[]): string[] {
        if (value && typeof value === "string") {
            return [value];
        }

        return value as string[];
    }

}
